import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Database, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Drawer from '@/components/ui/Drawer';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { accessApi } from '@/services/access-api';
import type { MemberDataSource } from '@/types';

interface DataAccessDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  member: { id: string; name: string; email: string } | null;
}

function healthDot(health: string): string {
  if (health === 'healthy') return 'bg-success';
  if (health === 'warning') return 'bg-warn';
  return 'bg-muted-foreground/40';
}

/**
 * Per-member data access editor (Workspaces Layer 3). Lists every connected
 * source in the workspace with a toggle for whether this member can query it.
 * Access is BINARY today (grant defaults to 'analyst'); finer viewer/analyst
 * tiers are a later refinement, so no role selector is shown here.
 */
const DataAccessDrawer: React.FC<DataAccessDrawerProps> = ({ isOpen, onClose, member }) => {
  const queryClient = useQueryClient();
  const userId = member?.id ?? '';
  const queryKey = ['memberDataAccess', userId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => accessApi.getMemberDataAccess(userId),
    enabled: isOpen && !!userId,
  });

  const sources = data?.sources ?? [];
  const sharedCount = sources.filter((s) => s.hasAccess).length;

  // Group by org only when the workspace spans more than one org.
  const groups = useMemo(() => {
    const byOrg = new Map<string, { name: string; items: MemberDataSource[] }>();
    for (const s of sources) {
      const g = byOrg.get(s.organizationId) ?? { name: s.organizationName, items: [] };
      g.items.push(s);
      byOrg.set(s.organizationId, g);
    }
    return Array.from(byOrg.values());
  }, [sources]);
  const multiOrg = groups.length > 1;

  const toggle = useMutation({
    mutationFn: ({ source, next }: { source: MemberDataSource; next: boolean }) =>
      next
        ? accessApi.grantSource(source.id, userId)
        : accessApi.revokeSource(source.id, userId),
    onMutate: async ({ source, next }) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<{ sources: MemberDataSource[] }>(queryKey);
      queryClient.setQueryData<{ sources: MemberDataSource[] }>(queryKey, (old) =>
        old
          ? { sources: old.sources.map((s) => (s.id === source.id ? { ...s, hasAccess: next } : s)) }
          : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
      toast.error('Could not update access. Please try again.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const renderSource = (s: MemberDataSource) => {
    const busy = toggle.isPending && toggle.variables?.source.id === s.id;
    return (
      <div key={s.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors">
        <div className="w-9 h-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center flex-shrink-0">
          <Database className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground truncate">{s.name}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
            <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', healthDot(s.healthStatus))} />
            <span className="capitalize">{s.sourceType.replace(/[-_]/g, ' ')}</span>
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={s.hasAccess}
          aria-label={`Access to ${s.name}`}
          disabled={busy}
          onClick={() => toggle.mutate({ source: s, next: !s.hasAccess })}
          className={clsx(
            'relative w-10 h-6 rounded-full flex-shrink-0 transition-colors disabled:opacity-60',
            s.hasAccess ? 'bg-primary' : 'bg-input',
          )}
        >
          <span
            className={clsx(
              'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-card shadow transition-transform',
              s.hasAccess && 'translate-x-4',
            )}
          />
          {busy && (
            <Loader2 className="w-3 h-3 animate-spin absolute inset-0 m-auto text-primary-foreground" />
          )}
        </button>
      </div>
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Data access" width="md">
      <div className="flex flex-col h-full">
        <div className="px-5 py-4 border-b border-border flex-shrink-0">
          <p className="text-sm font-medium text-foreground truncate">{member?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{member?.email}</p>
          {!isLoading && (
            <p className="text-xs text-muted-foreground mt-2">
              Shared:{' '}
              <span className="font-semibold text-foreground tabular-nums">{sharedCount}</span> of{' '}
              <span className="font-semibold text-foreground tabular-nums">{sources.length}</span>{' '}
              connected {sources.length === 1 ? 'source' : 'sources'}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner />
            </div>
          ) : sources.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              No connected sources in this workspace yet.
            </p>
          ) : multiOrg ? (
            <div className="space-y-4">
              {groups.map((g) => (
                <div key={g.name}>
                  <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {g.name}
                  </p>
                  {g.items.map(renderSource)}
                </div>
              ))}
            </div>
          ) : (
            <div>{sources.map(renderSource)}</div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex-shrink-0">
          <p className="text-xs text-muted-foreground leading-relaxed">
            New sources are shared with the whole team by default. Turn one off to restrict it to
            specific people.
          </p>
        </div>
      </div>
    </Drawer>
  );
};

export default DataAccessDrawer;
